import {Instant} from "@js-joda/core";
import {Jwt} from "./Jwt";
import {KiziUser} from "./KiziUser";
import {Serializable} from "../../libraries/util/Serializable";
import {AuthAggregatedPolicy} from "../../backend/authorization/models/AuthAggregatedPolicy";

export class UserSession implements Serializable {
	constructor(
		readonly idToken: string,
		readonly accessToken: string,
		readonly startedAt: Instant,
		readonly expiresAt: Instant,
		readonly tokenType: string,
		readonly jwt: Jwt,
		readonly user: KiziUser,
		readonly policy: AuthAggregatedPolicy | null,
	) {
	}
	
	static deserialize(json: any): UserSession {
		return new UserSession(
			json.idToken,
			json.accessToken,
			Instant.ofEpochSecond(json.startedAt, 0),
			Instant.ofEpochSecond(json.expiresAt, 0),
			json.tokenType,
			Jwt.deserialize(json.jwt),
			KiziUser.deserialize(json.user),
			json.policy ? JSON.parse(json.policy) : null
		);
	}
	
	serialize(): any {
		return {
			idToken: this.idToken,
			accessToken: this.accessToken,
			startedAt: this.startedAt.epochSecond(),
			expiresAt: this.expiresAt.epochSecond(),
			tokenType: this.tokenType,
			jwt: this.jwt.serialize(),
			user: this.user.serialize(),
			policy: JSON.stringify(this.policy),
		};
	}
	
	hasPermissionFor(action: string): boolean {
		if (!this.policy)
			return false;
		
		return this.policy.rules
			.filter(it => it.effect === "ALLOW")
			.flatMap(it => it.actions)
			.some(it => matches(it, action));
	}
}

function matches(pattern: string, value: string): boolean {
	if (pattern === "*")
		return true;
	if (isPattern(pattern))
		return patternMatches(pattern, value);
	return pattern === value;
}

function patternMatches(pattern: string, value: string): boolean {
	const regex = toRegex(pattern);
	return regex.test(value);
}

function isPattern(pattern: string): boolean {
	return pattern.indexOf(",") >= 0 || pattern.indexOf("*") >= 0;
}

function toRegex(pattern: string): RegExp {
	return new RegExp(toRegexPattern(pattern));
}

function toRegexPattern(pattern: string): string {
	// escape to avoid leaking accidental regex operations
	const escaped = escapeRegExp(pattern);
	
	// process wildcards: krn:* -> krn:.*
	const withWildcards = escaped.replaceAll("\\*", ".*");
	
	// process commas: krn:a,b,c:foo -> krn:(a|b|c):foo
	const withCommaDelimited = withWildcards
		// split krn into parts
		.split(":")
		// for each part that contains a comma
		.map(it => it.indexOf(",") >= 0
			// surround it with parens and replace the comma with an OR symbol
			? `(${it.replaceAll(",", "|")})`
			: it)
		// join back into a string
		.join(":");
	
	// prepend ^ and append $ to ensure a full match
	const wrapped = `^${withCommaDelimited}$`;
	
	return wrapped;
}

// https://stackoverflow.com/a/6969486
function escapeRegExp(value: string): string {
	return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
}
